import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  IconButton,
  MenuItem,
  Paper,
  Popover,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { useGetCodesQuery } from 'src/api/codes/getCodes';
import { useDeleteCodeMutation } from 'src/api/codes/deleteCode';
import Iconify from 'src/components/iconify';
import DeleteDialog from 'src/components/pickem-admin/DeleteDialog';
import { CodeDialog } from './code.modal';

type SortField = 'id' | 'code' | 'name' | 'status' | 'ordering';
type SortOrder = 'asc' | 'desc';

export const CodesSettings = () => {
  const [open, setOpen] = useState(null);
  const [openId, setOpenId] = useState<number>(-1);
  const [addItem, setAddItem] = useState(false);
  const [editItemId, setEditItemId] = useState<number>(-1);
  const [deleteItemId, setDeleteItemId] = useState<number>(-1);
  const [sortField, setSortField] = useState<SortField>('ordering');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  // * API
  const { data: codes = [], isLoading: isLoadingCodes } = useGetCodesQuery();

  const sortedCodes = useMemo(() => {
    return [...codes].sort((a, b) => {
      const aVal = a[sortField] ?? '';
      const bVal = b[sortField] ?? '';
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [codes, sortField, sortOrder]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };
  const { mutateAsync: deleteCode, isPending: isDeletePending } =
    useDeleteCodeMutation();

  const headers = [
    { field: 'id', headerName: 'ID', sortable: true },
    { field: 'code', headerName: 'Code', sortable: true },
    { field: 'name', headerName: 'Name', sortable: true },
    { field: 'logo', headerName: 'Logo', sortable: false },
    { field: 'ordering', headerName: 'Order', sortable: true },
    { field: 'status', headerName: 'Status', sortable: true },
    {
      field: 'actions',
      headerName: 'Actions',
      align: 'right',
      sortable: false,
    },
  ];

  const handleMenu = (event: any) => {
    if (event?.currentTarget?.id) {
      setOpen(event.currentTarget);
      setOpenId(Number(event.currentTarget.id));
    } else {
      setOpen(null);
      setOpenId(-1);
    }
  };

  const deleteCodeHandler = (id: number) => {
    setOpen(null);
    setOpenId(id);

    deleteCode(id);
  };

  const closeDeleteModal = () => {
    setOpen(null);
    setOpenId(-1);
    setDeleteItemId(-1);
  };

  const closeModal = () => {
    setOpen(null);
    setOpenId(-1);
    setAddItem(false);
    setEditItemId(-1);
  };

  return (
    <Container>
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <Typography variant='h4'>Sports</Typography>

        <Button
          variant='contained'
          startIcon={<Iconify icon='eva:plus-fill' />}
          onClick={() => setAddItem(true)}
        >
          New Sport
        </Button>
      </Stack>
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box sx={{ minHeight: 350 }}>
            {isLoadingCodes ? (
              <Skeleton width='100%' height={120} />
            ) : (
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label='sports table'>
                  <TableHead>
                    <TableRow>
                      {headers.map((header) => (
                        <TableCell key={header.field}>
                          {header.sortable ? (
                            <TableSortLabel
                              active={sortField === header.field}
                              direction={
                                sortField === header.field ? sortOrder : 'asc'
                              }
                              onClick={() =>
                                handleSort(header.field as SortField)
                              }
                            >
                              {header.headerName}
                            </TableSortLabel>
                          ) : (
                            header.headerName
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!sortedCodes.length ? (
                      <TableRow>
                        <TableCell colSpan={headers.length} align='center'>
                          No codes found
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedCodes.map((row) => (
                        <TableRow
                          key={row.id}
                          sx={{
                            '&:last-child td, &:last-child th': { border: 0 },
                          }}
                        >
                          {headers.map((header) => {
                            switch (header.field) {
                              case 'logo':
                                return (
                                  <TableCell>
                                    {row.logo && (
                                      <img
                                        src={row.logo}
                                        alt={row.name}
                                        height={40}
                                      />
                                    )}
                                  </TableCell>
                                );
                              case 'actions':
                                return (
                                  <TableCell align='right' key={header.field}>
                                    <IconButton
                                      size='large'
                                      color='inherit'
                                      onClick={handleMenu}
                                      id={String(row.id)}
                                    >
                                      <Iconify
                                        icon={'eva:more-vertical-fill'}
                                      />
                                    </IconButton>

                                    <Popover
                                      open={Boolean(open)}
                                      anchorEl={open}
                                      onClose={handleMenu}
                                      anchorOrigin={{
                                        vertical: 'top',
                                        horizontal: 'left',
                                      }}
                                      transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                      }}
                                    >
                                      <MenuItem
                                        onClick={() => setEditItemId(openId)}
                                      >
                                        <Iconify
                                          icon={'eva:edit-outline'}
                                          sx={{ mr: 1 }}
                                        />
                                        Edit
                                      </MenuItem>
                                      <MenuItem
                                        onClick={() => setDeleteItemId(openId)}
                                      >
                                        <Iconify
                                          icon={'eva:trash-outline'}
                                          sx={{ mr: 1 }}
                                        />
                                        Delete
                                      </MenuItem>
                                    </Popover>
                                  </TableCell>
                                );
                              case 'status':
                                return (
                                  <TableCell key={header.field}>
                                    <Chip
                                      label={row.status}
                                      color={
                                        row.status === 'active'
                                          ? 'primary'
                                          : 'default'
                                      }
                                    />
                                  </TableCell>
                                );
                              default:
                                return (
                                  <TableCell key={header.field}>
                                    {row[header.field as keyof typeof row]}
                                  </TableCell>
                                );
                            }
                          })}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </CardContent>
      </Card>

      {(addItem || editItemId !== -1) && (
        <CodeDialog
          open={addItem || editItemId !== -1}
          code={codes?.find((code) => code.id === openId)}
          onClose={closeModal}
        />
      )}

      {deleteItemId !== -1 && (
        <DeleteDialog
          openConfirm={deleteItemId !== -1}
          type='Code'
          index={deleteItemId}
          setOpenConfirm={closeDeleteModal}
          deleteGameHandler={deleteCodeHandler}
          isLoading={isDeletePending}
        />
      )}
    </Container>
  );
};
